import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Dropdown,
  Button,
  TextInput,
  Toggle,
} from "@carbon/react";

const Amenities = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 4,
  lastSavedStep = 0,
  onStepChange,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  

  //  EDIT MODE CALCULATION

  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  // FIELD MAP 
  const FIELD_MAP = useMemo(() => ({
    bathrooms: "bathrooms",
    balcony: "balcony", 
    waterSupply: "water_supply",
    gym: "gym",
    gatedSecurity: "gated_security",
    propertyCondition: "property_condition",
    petAllowed: "pet_allowed",
    nonVegAllowed: "non_veg_allowed",
  }), []);

  // STATE
  const [form, setForm] = useState({
    bathrooms: 1,
    balcony: 0,
    waterSupply: "",
    gym: false,
    gatedSecurity: false,
    propertyCondition: "",
    petAllowed: false,
    nonVegAllowed: false,
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // FIELD UPDATES
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );


  const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

  // LOAD INITIAL DATA 

  useEffect(() => {
    if (!data?.amenities) {
      return;
    }
    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = data.amenities[apiKey];
      if (raw == null) {
        console.log(`  - ${field}: No data for ${apiKey}`);
        return;
      }

      console.log(`  - ${field}: raw value =`, raw, 'type:', typeof raw);

      // Handle boolean fields
      if (
        field === "gym" || 
        field === "gatedSecurity" || 
        field === "similarPropertiesAvailable" ||
        field === "petAllowed" ||
        field === "nonVegAllowed"
      ) {
        // Convert various truthy values to boolean
        incoming[field] = Boolean(
          raw === true || 
          raw === 1 || 
          raw === "true" || 
          raw === "1" || 
          raw === "yes"
        );
      }
      // Handle amenities array
      else if (field === "amenities") {
        if (Array.isArray(raw)) {
          incoming[field] = raw.map((x) => String(x));
        } else if (typeof raw === 'string') {
          incoming[field] = raw.split(',').map(x => String(x.trim())).filter(x => x);
        } else {
          incoming[field] = [String(raw)];
        }
      }
      // Handle numeric fields (bathrooms, balcony)
      else if (field === "bathrooms" || field === "balcony") {
        incoming[field] = Number(raw) || 0;
      }
      // Handle other fields (waterSupply, propertyCondition)
      else {
        incoming[field] = String(raw);
      }
    });
    setForm((prev) => {
      const newForm = { ...prev, ...incoming };
      return newForm;
    });
  }, [data, FIELD_MAP]);
  
  
  // SAVE HANDLER 
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 Rent Amenities handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form 
    });

    // Mark all fields as touched for validation
    setTouched(Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {}));

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      // Create property if it doesn't exist
      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Rent Amenities Step...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      if (!uuid) {
        throw new Error("Property UUID is required");
      }

      // Build payload according to API schema
      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const value = form[field];
        
        if (field === "amenities") {
          if (Array.isArray(value) && value.length > 0) {
            payload[apiKey] = value.map(id => Number(id)); // Convert to numbers
          }
        } 
        // Handle boolean fields (convert to 1/0)
        else if (
          field === "gym" || 
          field === "gatedSecurity" || 
          field === "similarPropertiesAvailable" ||
          field === "petAllowed" ||
          field === "nonVegAllowed"
        ) {
          payload[apiKey] = value ? 1 : 0;
        }
        // Handle numeric fields
        else if (field === "bathrooms" || field === "balcony") {
          payload[apiKey] = Number(value) || 0;
        }
        // Handle dropdown/select fields
        else if (field === "waterSupply" || field === "propertyCondition") {
          payload[apiKey] = value ? Number(value) : "";
        }
        // Handle text fields
        else {
          payload[apiKey] = String(value || "");
        }
      }

      console.log('💾 Saving rent amenities:', payload);
      await saveSectionAttributes(uuid, payload, form, "amenities");

      setSuccess("Amenities saved successfully!");
      
      // Call parent's onSaveSuccess with step number
      onSaveSuccess?.(uuid, form, currentStep);
      
      // Call onComplete if provided
      onComplete?.(form);

      // Navigate to next step if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save amenities");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext
  ]);

  
  // BUTTON HANDLERS

  const handleSaveOnly = useCallback((e) => {
  //  e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
  //  e?.preventDefault();
    handleSave(true);
  }, [handleSave]);


const renderCounter = (
  fieldKey,
  label,
  min = 0,
  required = true
) => {
    const value = form[fieldKey] || 0;
    const error = touched[fieldKey] && (value < min ? `${label} must be at least ${min}` : null);

    return (
      <div>
        <label style={{ fontWeight: "600", display: "block", marginBottom: "0.5rem" }}>
          {required ? requiredLabel(label) : label}
        </label>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <Button
            kind="secondary"
            size="sm"
            onClick={() => updateField(fieldKey, Math.max(min, value - 1))}
            disabled={value <= min}
          >
            -
          </Button>
          <TextInput
            id={fieldKey}
            value={value}
            readOnly
            style={{ width: "80px", textAlign: "center" }}
            invalid={!!error}
            invalidText={error}
          />
          <Button
            kind="secondary"
            size="sm"
            onClick={() => updateField(fieldKey, value + 1)}
          >
            +
          </Button>
        </div>
      </div>
    );
  };

  // Toggle component
  const renderToggle = (fieldKey, label, required = false) => {
    const value = form[fieldKey];

    return (
      <div style={{ marginTop: "1rem" }}>
        <Toggle
          id={`toggle-${fieldKey}`}
          labelText={required ? requiredLabel(label) : label}
          labelA="No"
          labelB="Yes"
          toggled={!!value}
          onToggle={(checked) => updateField(fieldKey, checked)}
        />
      </div>
    );
  };

  // Dropdown component
  const renderDropdown = (fieldKey, label) => {
    const options = getOptions(fieldKey);
    const selectedValue = form[fieldKey];
    const selectedItem = options.find(opt => String(opt.id) === String(selectedValue));

    return (
      <Dropdown
        id={fieldKey}
        titleText={label}
        label={`Select ${label}`}
        items={options}
        itemToString={(item) => item?.label ?? ""}
        selectedItem={selectedItem || null}
        onChange={({ selectedItem }) => 
          updateField(fieldKey, selectedItem?.id ?? "")
        }
      />
    );
  };


  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column sm={4} md={4} lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        
        
          <p
            style={{
              color: "#0f62fe",
              fontSize: "0.95rem",
              fontWeight: 500,
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: "1.5",
            }}
          >
            Provide additional information about your property to improve visibility and attract more tenants or buyers.
          </p>
        </div>
            <Stack gap={6}>
              {/* INFRASTRUCTURE */}
              <FormGroup>
             
                <Grid>
                  <Column sm={4} md={4} lg={5}>
                    {renderCounter("bathrooms", "Bathroom(s)", 1, true)}
                  </Column>
                  <Column sm={4} md={4}  lg={5}>
                    {renderCounter("balcony", "Balcony(s)", 0, false)}
                  </Column>
                  <Column sm={4} md={4} lg={5}>
                    {renderDropdown("waterSupply", "Water Supply")}
                  </Column>
                  <Column sm={4} md={4} lg={5}>
                    {renderToggle("petAllowed", "Pets Allowed", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* FACILITIES */}
              <FormGroup legendText="Facilities">
                <Grid>
                  <Column sm={4} md={4} lg={5}>
                    {renderToggle("gym", "Gym Facility", false)}
                  </Column>
                  <Column  sm={4} md={4}lg={5}>
                    {renderToggle("gatedSecurity", "Gated Security", false)}
                  </Column>
                  <Column sm={4} md={4} lg={5}>
                    {renderDropdown("propertyCondition", "Property Condition" ,)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* TENANT RULES */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={5}>
                    {renderToggle("nonVegAllowed", "Non-Veg Allowed", false)}
                  </Column>
               
                </Grid>
              </FormGroup>


              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave={true}
                  showNext={true}
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;